/**
 * IconRegistry - Centralized icon management for KnowledgeBase
 * Supports Material Icons, Lucide Icons, FontAwesome, and custom image URLs
 *
 * Obsidian-compatible icon syntax:
 * - :icon-name: for Material Icons
 * - :lucide-sword: for Lucide Icons
 * - :fa-dragon: for FontAwesome
 * - :img-https://example.com/icon.png: for custom images
 */

import { ICON_TYPES } from './types'
import { MATERIAL_ICONS } from './material'
import { MATERIAL_SYMBOLS } from './material-symbols'
import { LUCIDE_ICONS } from './lucide'
import { FONTAWESOME_ICONS } from './fontawesome'
import { EMOJI_ICONS } from './emoji'
import { SYMBOL_ICONS } from './symbol'
import { ICON_CATEGORIES } from './categories'
import { parseIconString, iconToString, getIconHTML } from './utils'

/**
 * Default configuration for enabled icon types
 */
const DEFAULT_CONFIG = {
  [ICON_TYPES.MATERIAL]: true,
  [ICON_TYPES.MATERIAL_SYMBOLS]: true,
  [ICON_TYPES.LUCIDE]: true,
  [ICON_TYPES.FONTAWESOME]: true,
  [ICON_TYPES.IMAGE]: true,
  [ICON_TYPES.EMOJI]: true,
  [ICON_TYPES.SYMBOL]: true
}

/**
 * IconRegistry class with enable/disable functionality
 */
class IconRegistry {
  constructor(config = {}) {
    this._config = { ...DEFAULT_CONFIG, ...config }
    this._customIcons = new Map()
  }

  /**
   * Get current configuration
   */
  get config() {
    return { ...this._config }
  }

  /**
   * Enable an icon type
   * @param {string} type - Icon type from ICON_TYPES
   */
  enable(type) {
    if (Object.values(ICON_TYPES).includes(type)) {
      this._config[type] = true
    }
    return this
  }

  /**
   * Disable an icon type
   * @param {string} type - Icon type from ICON_TYPES
   */
  disable(type) {
    if (Object.values(ICON_TYPES).includes(type)) {
      this._config[type] = false
    }
    return this
  }

  /**
   * Toggle an icon type
   * @param {string} type - Icon type from ICON_TYPES
   */
  toggle(type) {
    if (Object.values(ICON_TYPES).includes(type)) {
      this._config[type] = !this._config[type]
    }
    return this
  }

  /**
   * Check if an icon type is enabled
   * @param {string} type - Icon type from ICON_TYPES
   */
  isEnabled(type) {
    return this._config[type] === true
  }

  /**
   * Set multiple icon types at once
   * @param {Object} config - Configuration object { [ICON_TYPE]: boolean }
   */
  setConfig(config) {
    Object.entries(config).forEach(([type, enabled]) => {
      if (Object.values(ICON_TYPES).includes(type)) {
        this._config[type] = Boolean(enabled)
      }
    })
    return this
  }

  /**
   * Reset configuration to defaults
   */
  resetConfig() {
    this._config = { ...DEFAULT_CONFIG }
    return this
  }

  /**
   * Get list of enabled icon types
   */
  getEnabledTypes() {
    return Object.entries(this._config)
      .filter(([, enabled]) => enabled)
      .map(([type]) => type)
  }

  /**
   * Get list of disabled icon types
   */
  getDisabledTypes() {
    return Object.entries(this._config)
      .filter(([, enabled]) => !enabled)
      .map(([type]) => type)
  }

  /**
   * Get icons for a specific type (respects enabled/disabled state)
   * @param {string} type - Icon type from ICON_TYPES
   */
  getIconsForType(type) {
    if (!this.isEnabled(type)) return []

    switch (type) {
      case ICON_TYPES.MATERIAL:
        return MATERIAL_ICONS
      case ICON_TYPES.MATERIAL_SYMBOLS:
        return MATERIAL_SYMBOLS
      case ICON_TYPES.LUCIDE:
        return LUCIDE_ICONS
      case ICON_TYPES.FONTAWESOME:
        return FONTAWESOME_ICONS
      case ICON_TYPES.EMOJI:
        return EMOJI_ICONS
      case ICON_TYPES.SYMBOL:
        return SYMBOL_ICONS
      default:
        return []
    }
  }

  /**
   * Get all available icons across enabled types
   */
  getAllIcons() {
    const icons = []

    if (this.isEnabled(ICON_TYPES.MATERIAL)) {
      MATERIAL_ICONS.forEach((icon) => {
        icons.push({ type: ICON_TYPES.MATERIAL, value: icon, label: icon })
      })
    }

    if (this.isEnabled(ICON_TYPES.MATERIAL_SYMBOLS)) {
      MATERIAL_SYMBOLS.forEach((icon) => {
        icons.push({ type: ICON_TYPES.MATERIAL_SYMBOLS, value: icon, label: `ms:${icon}` })
      })
    }

    if (this.isEnabled(ICON_TYPES.LUCIDE)) {
      LUCIDE_ICONS.forEach((icon) => {
        icons.push({ type: ICON_TYPES.LUCIDE, value: icon, label: `lucide:${icon}` })
      })
    }

    if (this.isEnabled(ICON_TYPES.FONTAWESOME)) {
      FONTAWESOME_ICONS.forEach((icon) => {
        icons.push({ type: ICON_TYPES.FONTAWESOME, value: icon, label: icon })
      })
    }

    if (this.isEnabled(ICON_TYPES.EMOJI)) {
      EMOJI_ICONS.forEach((icon) => {
        icons.push({ type: ICON_TYPES.EMOJI, value: icon.value, label: `emoji:${icon.label}` })
      })
    }

    if (this.isEnabled(ICON_TYPES.SYMBOL)) {
      SYMBOL_ICONS.forEach((icon) => {
        icons.push({ type: ICON_TYPES.SYMBOL, value: icon.value, label: `symbol:${icon.label}` })
      })
    }

    return icons
  }

  /**
   * Check if a string is a valid icon reference (respects enabled/disabled state)
   * @param {string} iconStr - Icon string to validate
   */
  isValidIcon(iconStr) {
    const parsed = parseIconString(iconStr)
    if (!parsed) return false

    // Check if the icon type is enabled
    if (!this.isEnabled(parsed.type)) return false

    switch (parsed.type) {
      case ICON_TYPES.MATERIAL:
        return MATERIAL_ICONS.includes(parsed.value) || parsed.value.length > 0
      case ICON_TYPES.MATERIAL_SYMBOLS:
        return MATERIAL_SYMBOLS.includes(parsed.value) || parsed.value.length > 0
      case ICON_TYPES.LUCIDE:
        return LUCIDE_ICONS.includes(parsed.value) || parsed.value.length > 0
      case ICON_TYPES.FONTAWESOME:
        return parsed.value.length > 0
      case ICON_TYPES.IMAGE:
        return parsed.value.startsWith('http://') || parsed.value.startsWith('https://')
      case ICON_TYPES.EMOJI:
        return (
          EMOJI_ICONS.some((e) => e.value === parsed.value || e.label === parsed.value) ||
          parsed.value.length > 0
        )
      case ICON_TYPES.SYMBOL:
        return (
          SYMBOL_ICONS.some((s) => s.value === parsed.value || s.label === parsed.value) ||
          parsed.value.length > 0
        )
      default:
        return false
    }
  }

  /**
   * Search icons across enabled icon sets
   * @param {string} query - Search query
   * @param {Object} options - Search options
   */
  searchIcons(query, options = {}) {
    const { limit = 50, types = null } = options
    const q = (query || '').toLowerCase().trim()
    const results = []

    const shouldInclude = (type) => {
      if (!this.isEnabled(type)) return false
      if (types && !types.includes(type)) return false
      return true
    }

    if (shouldInclude(ICON_TYPES.MATERIAL)) {
      MATERIAL_ICONS.forEach((icon) => {
        if (!q || icon.includes(q)) {
          results.push({ type: ICON_TYPES.MATERIAL, value: icon, label: icon })
        }
      })
    }

    if (shouldInclude(ICON_TYPES.MATERIAL_SYMBOLS)) {
      MATERIAL_SYMBOLS.forEach((icon) => {
        if (!q || icon.includes(q)) {
          results.push({ type: ICON_TYPES.MATERIAL_SYMBOLS, value: icon, label: `ms:${icon}` })
        }
      })
    }

    if (shouldInclude(ICON_TYPES.LUCIDE)) {
      LUCIDE_ICONS.forEach((icon) => {
        if (!q || icon.includes(q)) {
          results.push({ type: ICON_TYPES.LUCIDE, value: icon, label: `lucide:${icon}` })
        }
      })
    }

    if (shouldInclude(ICON_TYPES.FONTAWESOME)) {
      FONTAWESOME_ICONS.forEach((icon) => {
        if (!q || icon.includes(q)) {
          results.push({ type: ICON_TYPES.FONTAWESOME, value: icon, label: icon })
        }
      })
    }

    if (shouldInclude(ICON_TYPES.EMOJI)) {
      EMOJI_ICONS.forEach((icon) => {
        if (!q || icon.label.includes(q) || icon.value.includes(q)) {
          results.push({ type: ICON_TYPES.EMOJI, value: icon.value, label: `emoji:${icon.label}` })
        }
      })
    }

    if (shouldInclude(ICON_TYPES.SYMBOL)) {
      SYMBOL_ICONS.forEach((icon) => {
        if (!q || icon.label.includes(q) || icon.value.includes(q)) {
          results.push({
            type: ICON_TYPES.SYMBOL,
            value: icon.value,
            label: `symbol:${icon.label}`
          })
        }
      })
    }

    return results.slice(0, limit)
  }

  /**
   * Register a custom icon
   * @param {string} id - Unique identifier
   * @param {Object} icon - Icon definition { type, value, label? }
   */
  registerCustomIcon(id, icon) {
    this._customIcons.set(id, icon)
    return this
  }

  /**
   * Unregister a custom icon
   * @param {string} id - Icon identifier
   */
  unregisterCustomIcon(id) {
    this._customIcons.delete(id)
    return this
  }

  /**
   * Get a custom icon by ID
   * @param {string} id - Icon identifier
   */
  getCustomIcon(id) {
    return this._customIcons.get(id) || null
  }

  /**
   * Get all custom icons
   */
  getAllCustomIcons() {
    return Array.from(this._customIcons.entries()).map(([id, icon]) => ({
      id,
      ...icon
    }))
  }
}

// Create singleton instance
const iconRegistry = new IconRegistry()

// Legacy function exports that use the singleton
function isValidIcon(iconStr) {
  return iconRegistry.isValidIcon(iconStr)
}

function searchIcons(query, options = {}) {
  return iconRegistry.searchIcons(query, options)
}

// Re-export everything
export {
  // Singleton instance
  iconRegistry,
  // Class for creating custom instances
  IconRegistry,
  // Types
  ICON_TYPES,
  // Icon collections
  MATERIAL_ICONS,
  MATERIAL_SYMBOLS,
  LUCIDE_ICONS,
  FONTAWESOME_ICONS,
  EMOJI_ICONS,
  SYMBOL_ICONS,
  // Categories
  ICON_CATEGORIES,
  // Utility functions
  parseIconString,
  iconToString,
  getIconHTML,
  isValidIcon,
  searchIcons,
  // Config
  DEFAULT_CONFIG
}

export default iconRegistry
